'use client';

import styles from './QuestionnaireContent.module.css';
import type { QuestionnaireContentProps } from './QuestionnaireContent.types';
import { Shimmer, Pill, Flex, Heading } from '@/_components';
import { QuestionnaireFooter } from './components/QuestionnaireFooter';
import { QuestionnaireHeader } from './components/QuestionnaireHeader';
import { QuestionnaireNextSectionHeader } from './components/QuestionnaireNextSectionHeader';
import { useContext, useEffect, type FC } from 'react';
import { SectionsSwitch } from './components/SectionsSwitch';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';

export const QuestionnaireContent: FC<QuestionnaireContentProps> = ({
  isLoading,
  mode,
  questionnaire,
  questions,
  section,
}) => {
  const { setShowIncompleteNavigation } = useContext(
    ClientQuestionnaireContext,
  );

  const { setShowIncompletePreviewNavigation } = useContext(PreviewContext);

  const isDraft: boolean = Boolean(questionnaire?.is_draft);
  const isAdminPreview: boolean = mode === 'admin-preview';

  useEffect(() => {
    return () => {
      setShowIncompletePreviewNavigation(false);
      setShowIncompleteNavigation(false);
    };
  }, [setShowIncompletePreviewNavigation, setShowIncompleteNavigation]);

  return (
    <>
      <div id="questionnaire-next-section-header--background-space-container" />
      <Flex
        isColumn={true}
        isGrowing={true}
        className={styles.questionnaireContentWrapper}
      >
        <QuestionnaireNextSectionHeader mode={mode} />
        <Flex
          className={styles.questionnaireContent}
          isColumn={true}
          data-testid="QuestionnaireContent-main"
        >
          <QuestionnaireHeader>
            {isLoading ? (
              <Shimmer height={24} width={120} />
            ) : (
              <Heading level={5}>{section?.name}</Heading>
            )}

            <Flex gap={4}>
              {isAdminPreview && isDraft && (
                <Pill label="Preview" intent="info" iconLeft="eye" />
              )}
              {(questionnaire?.deposit_status === 'paid' ||
                questionnaire?.deposit_status === 'marked_paid') &&
                questionnaire.deposit_amount && (
                  <Pill
                    label={`Paid $${questionnaire.deposit_amount} deposit`}
                    intent="success"
                    iconLeft="check-circle"
                  />
                )}
              {questionnaire?.deposit_status === 'payment_pending' && (
                <Pill
                  label="Deposit pending"
                  intent="info"
                  iconLeft="spinner-gap"
                />
              )}
              {isAdminPreview && !isDraft && !questionnaire?.completed && (
                <Pill label="Sent to client" intent="info" iconLeft="user" />
              )}
              {isAdminPreview && !isDraft && questionnaire?.completed && (
                <Pill label="Completed" intent="success" iconLeft="user" />
              )}
            </Flex>
          </QuestionnaireHeader>

          <SectionsSwitch
            mode={mode}
            isLoading={isLoading}
            isReviewSection={false}
            section={section}
            questions={questions}
            questionnaire={questionnaire}
          />
        </Flex>
        <QuestionnaireFooter isLoading={isLoading} mode={mode} />
      </Flex>
    </>
  );
};
